import { ROUTES, profileRouteFor } from './routes';
import { ROLES, getPrimaryRole, type Role } from './roles';
import type { PermissionKey } from './permissions';
import { UI_STRINGS } from './ui-strings';
import { ENTITY_TYPES, type EntityType } from './entity-types';
import type { ActiveMembership, SessionUser } from '@/lib/types/auth.types';
import type { IssueReportOffer } from '@/lib/types/billing';
import { ISSUE_REPORT_OFFER_STATE } from './issue-report';

/**
 * Role-adaptive navigation (US-006/US-039). `AppNavbar` renders only the items
 * a user's roles/permissions permit (top-level and dropdown children are both
 * filtered the same way; `allowedRoles: []` means any authenticated user).
 *
 * Two independent, AND'd gates, since they answer different questions
 * (AUTHZ-1's three axes - role is identity/persona-tier, permission is a
 * revocable capability):
 * - `allowedRoles` - an identity-tier gate (e.g. the whole Super Admin console
 *   is platform-operator status, not a grantable capability - stays role-gated).
 * - `requiredPermission` - an optional capability gate for links that need
 *   one, layered on top of `allowedRoles` (AND'd, not a replacement for it).
 *   Neither Team Access entry uses it any more (see below) - both startup and
 *   investor personas always see the link, matching the route guards in
 *   `App.tsx`; a member holding neither `team.manage` nor `team.invite` still
 *   reaches the page and gets a read-only variant there instead of the link
 *   disappearing. An array is ANY-of, matching the backend's
 *   `RequirePermission`/`PermissionGuard` semantics, for any future link that
 *   does need this gate.
 *
 * An item with `children` renders as a dropdown (Figma 283:2108 "Network",
 * "Workspace"); dropdown parents have no route of their own.
 *
 * Startup entity members get a **Workspace** dropdown (Figma "Workspace Sub
 * Navigation": Individual Profile / Startup Profile / Team & Permissions) in
 * place of the standalone Profile link; investor entity members get the same
 * three-item shape (Individual Profile / Investor Profile / Team Access), and
 * drop the DATA Room link too. Every other persona keeps the flat Profile link
 * resolved by `profileRouteFor` (ADR FE-1). This entityType branch is
 * structural (axis #2, which items exist at all for this persona shape) -
 * never a permission or role gate.
 */
export interface NavItem {
  label: string;
  to?: string;
  allowedRoles: Role[];
  requiredPermission?: PermissionKey | PermissionKey[];
  children?: NavItem[];
  /** Opens in a new browser tab instead of navigating in-SPA (MYN-281, the admin console entry). */
  openInNewTab?: boolean;
  /** Shown only to users holding any admin role (MYN-144); the admin console entry. */
  requiresAdmin?: boolean;
}

/** The shared shape of the startup/investor Workspace dropdown - only its children differ. */
function workspaceItem(children: NavItem[]): NavItem {
  return { label: UI_STRINGS.NAV.WORKSPACE, allowedRoles: [], children };
}

/**
 * Workspace dropdown children per entity type - a typed map (mirrors
 * `TEAM_PAGE_FEATURES` in `TeamPermissionsPage.tsx`) instead of a sequential
 * `if (isStartup) ... if (isInvestor) ...`, so a third entity type getting
 * its own Workspace treatment is one entry here, not another branch.
 * `Partial` because a type with no entry (trusted_partner today) legitimately
 * gets no Workspace dropdown at all - that persona keeps the flat Profile
 * link instead (see `buildNavItems`).
 */
const WORKSPACE_CHILDREN_BY_ENTITY_TYPE: Partial<Record<EntityType, NavItem[]>> = {
  [ENTITY_TYPES.STARTUP]: [
    { label: UI_STRINGS.NAV.INDIVIDUAL_PROFILE, to: ROUTES.ACCOUNT, allowedRoles: [] },
    { label: UI_STRINGS.NAV.STARTUP_PROFILE, to: ROUTES.STARTUP_PROFILE, allowedRoles: [] },
    {
      // Team Access is no longer gated on team.manage/team.invite - the link
      // is always visible for any startup persona member (allowedRoles: []),
      // matching the investor Team Access entry below. TeamPermissionsPage
      // itself renders the read-only "view-only" variant for a member
      // holding neither permission, rather than the link disappearing.
      label: UI_STRINGS.NAV.STARTUP_TEAM_PERMISSIONS,
      to: ROUTES.STARTUP_TEAM,
      allowedRoles: [],
    },
  ],
  [ENTITY_TYPES.INVESTOR]: [
    { label: UI_STRINGS.NAV.INDIVIDUAL_PROFILE, to: ROUTES.ACCOUNT, allowedRoles: [] },
    { label: UI_STRINGS.NAV.INVESTOR_PROFILE, to: ROUTES.INVESTOR_PROFILE, allowedRoles: [] },
    // Same treatment as the startup Team Access child above - neither is
    // gated on team.manage/team.invite; every investor persona always
    // reaches the page (ProtectedRoute in App.tsx admits any
    // investor_admin/investor_member); TeamPermissionsPage itself renders a
    // read-only "view-only" variant for a member holding neither permission,
    // rather than the link disappearing.
    { label: UI_STRINGS.NAV.INVESTOR_TEAM_PERMISSIONS, to: ROUTES.INVESTOR_TEAM, allowedRoles: [] },
  ],
  // Trusted Partner Workspace dropdown (MYN-102/104): Individual Profile /
  // Partner Profile / Team Access, the same three-item shape as startup and
  // investor above. Team Access reuses the existing TeamPermissionsPage -
  // TEAM_PAGE_FEATURES already has a trusted_partner entry, so no new page
  // was needed, only this link and the PARTNER_TEAM route. Labels are plain
  // strings, matching this file's own existing mix (e.g. 'Events'/'Resources'
  // below) rather than routing through the already yellow-band ui-strings.ts.
  [ENTITY_TYPES.TRUSTED_PARTNER]: [
    { label: 'Individual Profile', to: ROUTES.ACCOUNT, allowedRoles: [] },
    { label: 'Partner Profile', to: ROUTES.PARTNER_PROFILE, allowedRoles: [] },
    { label: 'Team Access', to: ROUTES.PARTNER_TEAM, allowedRoles: [] },
  ],
};

/**
 * @param activeMembership - the session's active membership, or null
 */
export function buildNavItems(activeMembership: ActiveMembership | null): NavItem[] {
  const entityType = activeMembership?.entityType;
  const isInvestor = entityType === ENTITY_TYPES.INVESTOR;
  const isTrustedPartner = entityType === ENTITY_TYPES.TRUSTED_PARTNER;
  const workspaceChildren = entityType ? WORKSPACE_CHILDREN_BY_ENTITY_TYPE[entityType] : undefined;

  const network: NavItem = {
    label: UI_STRINGS.NAV.NETWORK,
    allowedRoles: [],
    children: [
      { label: 'Events', to: ROUTES.EVENTS, allowedRoles: [] },
      { label: 'Resources', to: ROUTES.RESOURCES, allowedRoles: [] },
      // Shown to every persona, Trusted Partners included (A-90, client
      // requirement 2026-10-01; reverses the earlier A-32 exclusion).
      { label: 'Trusted Partners', to: ROUTES.TRUSTED_PARTNERS, allowedRoles: [] },
      // Its own page since MYN-252 (client request), every persona like Trusted Partners.
      { label: 'Startup Perks', to: ROUTES.TRUSTED_PARTNERS_PERKS, allowedRoles: [] },
    ],
  };

  const items: NavItem[] = [{ label: 'Dashboard', to: ROUTES.DASHBOARD, allowedRoles: [] }];

  if (workspaceChildren) {
    items.push(workspaceItem(workspaceChildren));
  }

  items.push(network);

  // Flat top-level "Perks" link (FR-29, MYN-104), Trusted Partner only - the
  // partner's own Perks & Offers console, not the member-facing Perks page.
  // Confirmed nav order from the target screenshot: Dashboard, Workspace,
  // Network, Perks.
  if (isTrustedPartner) {
    items.push({ label: 'Perks', to: ROUTES.PARTNER_OFFERS, allowedRoles: [] });
  }

  // Data Room is a startup persona's link only - the MYN-102/104 target nav
  // has none for investors or partners. Every startup admin and member gets
  // it whatever their access (client, 2026-10-12): the page itself shows the
  // room, the seat state, or the Issue Report explainer.
  if (entityType === ENTITY_TYPES.STARTUP) {
    items.push({ label: UI_STRINGS.NAV.DATA_ROOM, to: ROUTES.DATA_ROOM, allowedRoles: [] });
  }

  // "Billing & Info" is a TOP-LEVEL item, not a Workspace child - settled by
  // the 2026-09-22 designs, and FR-41's AC was corrected to match. Startup
  // personas only (investors and partners have no paid tier at all), and role-
  // gated to the Startup Admin because the route is: mnr-standards treats a
  // link whose route refuses the clicker as the same defect as an unguarded
  // route with no link.
  if (!isInvestor && !isTrustedPartner && entityType) {
    items.push({
      label: UI_STRINGS.NAV.BILLING,
      to: ROUTES.BILLING,
      allowedRoles: [ROLES.STARTUP_ADMIN],
    });
  }

  if (!workspaceChildren) {
    items.push({ label: 'Profile', to: profileRouteFor(activeMembership), allowedRoles: [] });
  }

  // The console was previously reachable only by typing /admin: the route was
  // guarded but nothing linked to it. mnr-standards treats a guarded route with
  // no link, and a link with no guard, as equally wrong - AppNavbar filters this
  // item through isAdmin, and ProtectedRoute gates the route on the same check.
  // Opens in a new tab (MYN-281, FR-196): AdminLayout is deliberately separate
  // chrome from this nav (see its own doc comment), so this leaves the member
  // app's nav and footer untouched in the original tab rather than retrofitting
  // them into the admin shell.
  items.push({
    label: UI_STRINGS.NAV.ADMIN,
    to: ROUTES.ADMIN,
    // Any admin role, not just Super Admin (MYN-144): matches the route's
    // `requiresAdmin` gate so link and route never disagree.
    allowedRoles: [],
    requiresAdmin: true,
    openInNewTab: true,
  });

  return items;
}

/**
 * The navbar's primary CTA. `link` renders a routed button (individuals ->
 * Create Startup, MYN-46); `cancel` is its route-aware destructive counterpart
 * while the user is ON the create form (Figma 287:3098); `leave_journey`
 * replaces `cancel` while the Issue Report journey is in progress (FR-205,
 * A-240); `billing` renders the Issue Report invitation for startup admins;
 * `coming_soon` is that invitation with nothing to press while no Issue Report
 * plan is on sale (FR-203); `null` shows no CTA (e.g. super admin).
 */
export type PrimaryCta =
  | { kind: 'link'; label: string; to: string }
  | { kind: 'cancel'; label: string; to: string }
  | { kind: 'leave_journey'; label: string; to: string }
  | { kind: 'billing'; label: string; to: string }
  | { kind: 'coming_soon'; label: string };

/**
 * Label for any CTA that opens the create-startup wizard (navbar and dashboard
 * say the same thing, UX sheet r16/r154/r183): "Resume Startup Profile" when
 * the user has an in-progress draft, "Create Startup Profile" otherwise.
 */
export function createStartupCtaLabel(hasInProgressDraft: boolean): string {
  return hasInProgressDraft ? UI_STRINGS.NAV.RESUME_STARTUP : UI_STRINGS.NAV.CREATE_STARTUP;
}

/**
 * Single source for the navbar's primary CTA. Keyed on the user's primary role
 * via `getPrimaryRole` - NOT on raw `activeMembership`, which is `null` for both
 * individuals and super admins, so a raw check would wrongly offer super admins
 * "Create Startup Profile" (ADR FE-1: never re-derive persona from raw session state).
 * `pathname` makes the CTA route-aware: on the create-startup form the green
 * Create Startup flips to the pink Cancel Startup escape hatch. `hasInProgressDraft`
 * (the caller's own `drafts[user.id]`, via `hasInProgressDraft` in
 * `createStartupStore`) swaps the label to "Resume Startup Profile" instead of
 * adding a second CTA or a badge - same link, same route, just a label that
 * tells the individual they have something in progress.
 *
 * `hasPurchaseIntent` is true while the Issue Report journey is in progress
 * (FR-206): on the create form the Cancel action is then hidden and replaced
 * by "Back to Due Diligence page" (FR-205, A-240). Without it Cancel is
 * unchanged.
 */
export function resolvePrimaryCta(
  user: SessionUser | null,
  pathname?: string,
  hasInProgressDraft = false,
  hasPurchaseIntent = false,
): PrimaryCta | null {
  if (!user) return null;
  if (getPrimaryRole(user.roles) === ROLES.INDIVIDUAL) {
    if (pathname === ROUTES.CREATE_STARTUP) {
      return hasPurchaseIntent
        ? {
            kind: 'leave_journey',
            label: UI_STRINGS.NAV.BACK_TO_DUE_DILIGENCE,
            to: ROUTES.DUE_DILIGENCE_PLATFORM,
          }
        : { kind: 'cancel', label: UI_STRINGS.NAV.CANCEL_STARTUP, to: ROUTES.DASHBOARD };
    }
    return {
      kind: 'link',
      label: createStartupCtaLabel(hasInProgressDraft),
      to: ROUTES.CREATE_STARTUP,
    };
  }
  // Investor and Trusted Partner have no premium/paid tier at all (product
  // decision, same reasoning for both - neither is a fundraising subscriber)
  // - no CTA, not even a disabled one, for either persona.
  //
  // The Issue Report invitation (v1.2, FR-128; replaced "Upgrade to Premium").
  // A startup that already has data-room access - an Issue Report or SR NEXT
  // Premium - has nothing to buy, so `tier` gates it: `tier` is the backend's
  // own word (paid while an access period is open), never derived here. And
  // only a Startup Admin may buy (A-123): the purchase route is admin-guarded,
  // so offering the CTA to a member would send them to a page that refuses
  // them, which is worse than no CTA at all.
  // It opens the Data Room page, which explains the Issue Report before any
  // payment step (UX round 2, MYN-284); the dashboard card is the direct buy.
  if (
    user.tier === 'free' &&
    getPrimaryRole(user.roles) === ROLES.STARTUP_ADMIN &&
    user.activeMembership &&
    user.activeMembership.entityType !== ENTITY_TYPES.INVESTOR &&
    user.activeMembership.entityType !== ENTITY_TYPES.TRUSTED_PARTNER
  ) {
    return {
      kind: 'billing',
      label: UI_STRINGS.NAV.GET_ISSUE_REPORT,
      to: ROUTES.DATA_ROOM,
    };
  }
  return null;
}

/**
 * The Issue Report invitation with the public offer applied (FR-203): a buy CTA
 * shows only once the offer says a plan is on sale. While no plan is on sale it
 * becomes a non-clickable "Coming Soon", and while the offer is still loading
 * (or failed) it is left out, so a buy button never flashes and then turns into
 * "Coming Soon". Any other CTA is returned as it was.
 */
export function applyIssueReportOffer(
  cta: PrimaryCta | null,
  offer: IssueReportOffer | undefined,
): PrimaryCta | null {
  if (cta?.kind !== 'billing') return cta;
  if (offer?.state === ISSUE_REPORT_OFFER_STATE.COMING_SOON)
    return { kind: 'coming_soon', label: cta.label };
  return offer?.state === ISSUE_REPORT_OFFER_STATE.AVAILABLE ? cta : null;
}
